/**
 * Update Dialog Component
 *
 * Shows version update information with changelog and forces/allows updates
 */

import { memo } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ChangelogItem {
  type: 'fix' | 'new feature';
  title: string;
  media?: string[];
}

interface UpdateDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;

  /**
   * Version information
   */
  version: string;

  /**
   * Whether this is a forced update
   */
  isForced: boolean;

  /**
   * Changelog items
   */
  changelog: ChangelogItem[];

  /**
   * Callback when user clicks "Update now"
   */
  onUpdate: () => void;

  /**
   * Callback when user clicks "Later" (only available for non-forced updates)
   */
  onLater?: () => void;

  /**
   * Whether update is in progress
   */
  isUpdating?: boolean;
}

/**
 * Get display label for changelog item type
 */
const getTypeLabel = (type: ChangelogItem['type']): string => {
  switch (type) {
    case 'fix':
      return 'إصلاح';
    case 'new feature':
      return 'ميزة جديدة';
    default:
      return type;
  }
};

/**
 * Get CSS classes for changelog item type
 */
const getTypeClasses = (type: ChangelogItem['type']): string => {
  switch (type) {
    case 'fix':
      return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
    case 'new feature':
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
  }
};

export const UpdateDialog = memo<UpdateDialogProps>(
  ({
    open,
    version,
    isForced,
    changelog,
    onUpdate,
    onLater,
    isUpdating = false,
  }) => {
    return (
      <Dialog open={open} onOpenChange={undefined}>
        <DialogContent
          className="max-h-[80vh] overflow-hidden sm:max-w-[500px]"
          // Prevent closing if forced update
          onPointerDownOutside={
            isForced ? (e) => e.preventDefault() : undefined
          }
          onEscapeKeyDown={isForced ? (e) => e.preventDefault() : undefined}
        >
          <DialogHeader className="text-right">
            <DialogTitle className="text-right text-xl font-bold">
              إصدار جديد متاح
            </DialogTitle>
            <DialogDescription className="text-right text-base">
              الإصدار {version} متاح الآن مع تحسينات وميزات جديدة.
            </DialogDescription>
          </DialogHeader>

          {/* Changelog */}
          <div className="flex-1 overflow-y-auto py-4">
            <h3 className="mb-3 text-right text-sm font-semibold text-gray-900 dark:text-gray-100">
              ما الجديد:
            </h3>

            <div className="space-y-3">
              {changelog.map((item, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-end gap-2">
                    <span className="flex-1 text-right text-sm text-gray-700 dark:text-gray-300">
                      {item.title}
                    </span>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getTypeClasses(item.type)} `}
                    >
                      {getTypeLabel(item.type)}
                    </span>
                  </div>

                  {/* Media images */}
                  {item.media && item.media.length > 0 && (
                    <div className="flex flex-wrap justify-end gap-2">
                      {item.media.map((mediaUrl, mediaIndex) => (
                        <img
                          key={mediaIndex}
                          src={mediaUrl}
                          alt={`${item.title} - صورة ${mediaIndex + 1}`}
                          className="h-16 w-16 rounded-lg border border-gray-200 object-cover dark:border-gray-700"
                          loading="lazy"
                          onError={(e) => {
                            // Hide broken images
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {changelog.length === 0 && (
              <p className="text-right text-sm italic text-gray-500 dark:text-gray-400">
                لا توجد تفاصيل متاحة لهذا الإصدار.
              </p>
            )}
          </div>

          {/* Footer */}
          <DialogFooter className="flex-row-reverse gap-2">
            <Button
              onClick={onUpdate}
              disabled={isUpdating}
              className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
            >
              {isUpdating ? 'جاري التحديث...' : 'تحديث الآن'}
            </Button>

            {!isForced && onLater && (
              <Button
                variant="outline"
                onClick={onLater}
                disabled={isUpdating}
                className="flex-1"
              >
                لاحقاً
              </Button>
            )}
          </DialogFooter>

          {/* Forced update notice */}
          {isForced && (
            <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
              <p className="text-right text-xs text-amber-800 dark:text-amber-400">
                <strong>تحديث مطلوب:</strong> هذا التحديث مطلوب للمتابعة في
                استخدام التطبيق.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  },
);

UpdateDialog.displayName = 'UpdateDialog';
